import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { logCrud } from "@/services/activities";

interface PaymentMethod {
  id: string;
  nome: string;
  slug: string;
  icone: string | null;
}

interface BankAccount {
  id: string;
  nome: string;
  descricao: string | null;
  icone: string | null;
  status: string;
  bank_accounts_methods?: {
    payment_method_id: string;
  }[];
}

interface BankAccountManagerProps {
  onAccountCreated?: () => void;
  showHeadings?: boolean; // controla títulos e descrições dos cards
  stacked?: boolean; // quando true, listagem vem abaixo (uma coluna)
}

export function BankAccountManager({ onAccountCreated, showHeadings = true, stacked = false }: BankAccountManagerProps) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [accountName, setAccountName] = useState("");
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [physicalSuffix, setPhysicalSuffix] = useState("");
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
  const [relatedRecords, setRelatedRecords] = useState<{
    transactions: number;
    transactionPayments: number;
    settlementConfigs: number;
  } | null>(null);
  const [targetAccountId, setTargetAccountId] = useState<string>("");

  // Fetch payment methods
  const { data: rawPaymentMethods = [] } = useQuery({
    queryKey: ["payment_methods"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_methods")
        .select("*")
        .eq("status", "ativo")
        .order("nome");

      if (error) throw error;
      return data as PaymentMethod[];
    },
  });

  const paymentMethods = useMemo(() => {
    const moneyFirst = [...rawPaymentMethods];
    moneyFirst.sort((a, b) => {
      if (a.slug === "money") return -1;
      if (b.slug === "money") return 1;
      return a.nome.localeCompare(b.nome);
    });
    return moneyFirst;
  }, [rawPaymentMethods]);

  const moneyMethod = paymentMethods.find((method) => method.slug === "money");
  const moneyMethodId = moneyMethod?.id;

  const isMoneyOnlySelected = Boolean(
    moneyMethodId &&
      selectedMethods.length === 1 &&
      selectedMethods.includes(moneyMethodId)
  );

  // Cria conta padrão 'Caixa Físico' com método 'Dinheiro'
  const createCashPhysical = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error("Tenant não encontrado");

      const { data: newAccount, error: insertError } = await supabase
        .from("bank_accounts")
        .insert({ tenant_id: tenantId, nome: "Caixa Físico", status: "ativo" })
        .select()
        .single();
      if (insertError) throw insertError;

      const dinheiro = paymentMethods.find(m => m.slug === "money")
        || paymentMethods.find(m => m.nome?.toLowerCase?.() === "dinheiro");

      if (dinheiro && newAccount) {
        const { error: relError } = await supabase
          .from("bank_accounts_methods")
          .insert({ bank_account_id: newAccount.id, payment_method_id: dinheiro.id });
        if (relError) throw relError;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bank_accounts", tenantId] });
      toast.success("Conta 'Caixa Físico' criada!");
      void logCrud({ entitySlug: "entity:bank_accounts", operationSlug: "create", entityName: "Caixa Físico", screenSlug: "configuracoes/contas-bancarias" });
      setIntroDismissed(true);
      onAccountCreated?.();
    },
    onError: (error: any) => {
      toast.error("Erro ao criar conta padrão: " + error.message);
    }
  });

  // Fetch bank accounts
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank_accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from("bank_accounts")
        .select(`
          *,
          bank_accounts_methods (
            payment_method_id
          )
        `)
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as BankAccount[];
    },
    enabled: !!tenantId,
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error("Tenant não encontrado");
      if (!accountName.trim()) throw new Error("Nome da conta é obrigatório");

      if (editingId) {
        // Update existing account
        const updatedName =
          moneyMethodId && selectedMethods.length === 1 && selectedMethods[0] === moneyMethodId
            ? buildPhysicalName(physicalSuffix)
            : accountName;

        const { error: updateError } = await supabase
          .from("bank_accounts")
          .update({ nome: updatedName })
          .eq("id", editingId);

        if (updateError) throw updateError;

        // Delete existing relationships
        const { error: deleteError } = await supabase
          .from("bank_accounts_methods")
          .delete()
          .eq("bank_account_id", editingId);

        if (deleteError) throw deleteError;

        // Insert new relationships
        if (selectedMethods.length > 0) {
          const { error: insertError } = await supabase
            .from("bank_accounts_methods")
            .insert(
              selectedMethods.map((methodId) => ({
                bank_account_id: editingId,
                payment_method_id: methodId,
              }))
            );

          if (insertError) throw insertError;
        }
      } else {
        // Create new account
        const finalName =
          moneyMethodId && selectedMethods.length === 1 && selectedMethods[0] === moneyMethodId
            ? buildPhysicalName(physicalSuffix)
            : accountName;

        const { data: newAccount, error: insertError } = await supabase
          .from("bank_accounts")
          .insert({
            tenant_id: tenantId,
            nome: finalName,
            status: "ativo",
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Insert relationships
        if (selectedMethods.length > 0 && newAccount) {
          const { error: methodsError } = await supabase
            .from("bank_accounts_methods")
            .insert(
              selectedMethods.map((methodId) => ({
                bank_account_id: newAccount.id,
                payment_method_id: methodId,
              }))
            );

          if (methodsError) throw methodsError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_accounts", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["bank-accounts-with-methods"] });
      toast.success(editingId ? "Conta atualizada!" : "Conta cadastrada!");
      if (editingId) {
        void logCrud({ entitySlug: "entity:bank_accounts", operationSlug: "update", entityId: editingId, screenSlug: "configuracoes/contas-bancarias" });
      } else {
        void logCrud({ entitySlug: "entity:bank_accounts", operationSlug: "create", entityName: accountName, screenSlug: "configuracoes/contas-bancarias" });
      }
      resetForm();
      onAccountCreated?.();
    },
    onError: (error: any) => {
      toast.error("Erro ao salvar: " + error.message);
    },
  });

  // Check related records
  const checkRelatedRecords = async (accountId: string) => {
    if (!tenantId) return null;

    const [transactionsResult, transactionPaymentsResult, settlementConfigsResult] = await Promise.all([
      supabase
        .from("transactions")
        .select("id", { count: "exact", head: true })
        .eq("bank_account_id", accountId)
        .eq("tenant_id", tenantId),
      supabase
        .from("transaction_payments")
        .select("id", { count: "exact", head: true })
        .eq("bank_account_id", accountId)
        .eq("tenant_id", tenantId),
      supabase
        .from("payment_settlement_configs")
        .select("id", { count: "exact", head: true })
        .eq("bank_account_id", accountId)
        .eq("tenant_id", tenantId),
    ]);

    return {
      transactions: transactionsResult.count || 0,
      transactionPayments: transactionPaymentsResult.count || 0,
      settlementConfigs: settlementConfigsResult.count || 0,
    };
  };

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: async ({ fromAccountId, toAccountId }: { fromAccountId: string; toAccountId: string }) => {
      if (!tenantId) throw new Error("Tenant não encontrado");
      if (!toAccountId) throw new Error("Selecione uma conta de destino");

      // Transfer transactions
      const { error: transactionsError } = await supabase
        .from("transactions")
        .update({ bank_account_id: toAccountId })
        .eq("bank_account_id", fromAccountId)
        .eq("tenant_id", tenantId);

      if (transactionsError) throw transactionsError;

      // Transfer transaction_payments
      const { error: paymentsError } = await supabase
        .from("transaction_payments")
        .update({ bank_account_id: toAccountId })
        .eq("bank_account_id", fromAccountId)
        .eq("tenant_id", tenantId);

      if (paymentsError) throw paymentsError;

      // Transfer payment_settlement_configs
      const { error: configsError } = await supabase
        .from("payment_settlement_configs")
        .update({ bank_account_id: toAccountId })
        .eq("bank_account_id", fromAccountId)
        .eq("tenant_id", tenantId);

      if (configsError) throw configsError;
    },
    onSuccess: async () => {
      toast.success("Registros transferidos com sucesso!");
      // Now delete the account
      if (accountToDelete) {
        deleteMutation.mutate(accountToDelete);
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao transferir registros: " + (error?.message ?? "Tente novamente."));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bank_accounts")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_accounts", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["bank-accounts-with-methods"] });
      queryClient.invalidateQueries({ queryKey: ["payment-settlement-configs", tenantId] });
      toast.success("Conta excluída!");
      if (accountToDelete) {
        void logCrud({ entitySlug: "entity:bank_accounts", operationSlug: "delete", entityId: accountToDelete, screenSlug: "configuracoes/contas-bancarias" });
      }
      setDeleteId(null);
      setTransferModalOpen(false);
      setAccountToDelete(null);
      setRelatedRecords(null);
      setTargetAccountId("");
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir: " + (error?.message ?? "Tente novamente."));
    },
  });

  // Handle delete click
  const handleDeleteClick = async (accountId: string) => {
    const records = await checkRelatedRecords(accountId);
    
    if (records && (records.transactions > 0 || records.transactionPayments > 0 || records.settlementConfigs > 0)) {
      // Check if there are other accounts available for transfer
      const availableAccounts = bankAccounts.filter(
        (acc) => acc.id !== accountId && acc.status === "ativo"
      );
      
      if (availableAccounts.length === 0) {
        toast.error(
          "Não é possível excluir esta conta: existem registros vinculados e não há outras contas disponíveis para transferência."
        );
        return;
      }
      
      // Has related records, show transfer modal directly
      setAccountToDelete(accountId);
      setRelatedRecords(records);
      setTransferModalOpen(true);
    } else {
      // No related records, show confirmation dialog
      setDeleteId(accountId);
    }
  };

  const normalizePhysicalName = (name: string) => {
    const prefix = "Caixa Físico";
    const trimmed = name?.trim?.() ?? "";
    if (!trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return { isPhysical: false, suffix: "" };
    const remainder = trimmed.substring(prefix.length).trimStart();
    if (!remainder.startsWith("-")) {
      return { isPhysical: true, suffix: remainder };
    }
    const [, ...rest] = remainder.split("-");
    const suffix = rest.join("-").trim();
    return { isPhysical: true, suffix };
  };

  const buildPhysicalName = (suffix: string) => {
    const trimmed = suffix.trim();
    return trimmed ? `Caixa Físico - ${trimmed}` : "Caixa Físico";
  };

  const handleEdit = (account: BankAccount) => {
    setEditingId(account.id);
    setAccountName(account.nome);
    const methods = account.bank_accounts_methods?.map((m) => m.payment_method_id) || [];
    setSelectedMethods(methods);
    if (moneyMethodId && methods.includes(moneyMethodId)) {
      const { suffix } = normalizePhysicalName(account.nome);
      setPhysicalSuffix(suffix);
    } else {
      setPhysicalSuffix("");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setAccountName("");
    setSelectedMethods([]);
    setPhysicalSuffix("");
  };

  const handleMethodToggle = (methodId: string) => {
    if (methodId === moneyMethodId) {
      setSelectedMethods((prev) => {
        if (prev.includes(methodId)) {
          setPhysicalSuffix("");
          return prev.filter((id) => id !== methodId);
        }

        const normalized = normalizePhysicalName(accountName);
        const fallback = normalized.isPhysical
          ? normalized.suffix
          : accountName.trim();
        const nextSuffix = fallback || "";
        setPhysicalSuffix(nextSuffix);
        setAccountName(buildPhysicalName(nextSuffix));
        return moneyMethodId ? [moneyMethodId] : [];
      });
      return;
    }

    setSelectedMethods((prev) => {
      if (moneyMethodId && prev.includes(moneyMethodId)) {
        return prev;
      }
      return prev.includes(methodId)
        ? prev.filter((id) => id !== methodId)
        : [...prev, methodId];
    });
  };

  const getIconLabel = (icon?: string | null) => {
    if (!icon) return null;
    const normalized = icon.toLowerCase();
    if (normalized.includes("credit")) return "Cartão";
    if (normalized.includes("wallet")) return "Carteira";
    return icon;
  };

  return (
    <div
      className={stacked ? "grid grid-cols-1 md:grid-cols-2 gap-2" : "grid grid-cols-1 md:grid-cols-2 gap-2"}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    >
      {/* Formulário */}
      <Card>
        {showHeadings && (
          <CardHeader className="-mb-2">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              {editingId ? "Atualizar Conta" : "Cadastrar Conta"}
            </CardTitle>
            <CardDescription>
              {editingId
                ? "Edite os dados da conta bancária"
                : "Adicione uma nova conta bancária e selecione os métodos de pagamento"}
            </CardDescription>
          </CardHeader>
        )}
        <CardContent className="space-y-4 mt-2 px-3 pb-3">
          {bankAccounts.length === 0 && !introDismissed ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                <span className="font-bold text-xl">
                  Você trabalha com dinheiro vivo?
                  </span> 
                <br />
                Tem um caixa para armazená-los?
              </p>
              <div className="flex gap-2">
                <Button type="button" onClick={() => createCashPhysical.mutate()} disabled={createCashPhysical.isPending}>
                  {createCashPhysical.isPending ? "Criando..." : "Sim"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIntroDismissed(true)}>
                  Não
                </Button>
              </div>
            </div>
          ) : (
          <>
          <div className="space-y-1">
            <Label htmlFor="account-name">Nome da Conta</Label>
            {isMoneyOnlySelected ? (
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
                <div className="flex items-center rounded-md border bg-muted px-3 text-sm font-medium text-muted-foreground">
                  Caixa Físico
                </div>
                <Input
                  id="account-name"
                  placeholder="Identificação (ex.: Frente, NF, Principal)"
                  value={physicalSuffix}
                  onChange={(e) => setPhysicalSuffix(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            ) : (
              <Input
                id="account-name"
                placeholder="Ex: Caixa Físico, Nu Bank, etc."
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Métodos de Pagamento</Label>
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => {
                  // Seleciona automaticamente: Cartão de Crédito, Cartão de Débito e PIX
                  const wanted: Array<{ slug: string; names: string[] }> = [
                    { slug: "credit_card", names: ["cartão de crédito", "cartao de credito", "credito", "crédito"] },
                    { slug: "debit_card", names: ["cartão de débito", "cartao de debito", "debito", "débito"] },
                    { slug: "pix", names: ["pix"] },
                  ];
                  const ids: string[] = [];
                  for (const w of wanted) {
                    const found = paymentMethods.find(m => m.slug === w.slug)
                      || paymentMethods.find(m => w.names.includes((m.nome || "").toLowerCase()));
                    if (found) ids.push(found.id);
                  }
                  setSelectedMethods(prev => Array.from(new Set([...prev, ...ids])));
                }}
                aria-label="Selecionar métodos para conta digital"
              >
                É conta digital?
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {paymentMethods.map((method) => {
                const isMoney = method.id === moneyMethodId;
                const disabled = Boolean(
                  moneyMethodId &&
                    selectedMethods.includes(moneyMethodId) &&
                    method.id !== moneyMethodId
                );
                const checked = selectedMethods.includes(method.id);
                return (
                  <div key={method.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`method-${method.id}`}
                      checked={checked}
                      onCheckedChange={() => handleMethodToggle(method.id)}
                      disabled={disabled}
                    />
                    <label
                      htmlFor={`method-${method.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-2"
                    >
                      {method.icone && (
                        <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-medium">
                          {getIconLabel(method.icone)}
                        </Badge>
                      )}
                      <span>{method.nome}</span>
                      {isMoney && selectedMethods.includes(method.id) && (
                        <span className="text-xs text-muted-foreground">(exclusivo)</span>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Selecione os métodos que esta conta movimenta. Se marcar apenas Dinheiro, a conta será tratada como Caixa Físico.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending ||
                (!isMoneyOnlySelected && !accountName.trim() && !physicalSuffix.trim())
              }
              className="flex-1"
            >
              {saveMutation.isPending
                ? "Salvando..."
                : editingId
                ? "Atualizar"
                : "Cadastrar"}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            )}
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Listagem */}
      <Card>
          <CardHeader className="px-3 py-3">
            <CardTitle className="">Contas Cadastradas</CardTitle>
            {showHeadings && (
              <CardDescription className="">
                Gerencie suas contas bancárias
              </CardDescription>
            )}
          </CardHeader>
        <CardContent>
          {bankAccounts.length === 0 ? (
            <p className="text-left text-muted-foreground py-8">
              Nenhuma conta cadastrada
            </p>
          ) : (
            <div className="space-y-1">
              {bankAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-0 px-1 -mx-3 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium inline-block pr-3">{account.nome}</p>
                    <span className="inline-flex flex-wrap gap-1">
                      {paymentMethods
                        .filter((method) =>
                          account.bank_accounts_methods?.some(
                            (m) => m.payment_method_id === method.id
                          )
                        )
                        .map((method) => (
                          <Badge
                            key={method.id}
                            variant="outline"
                            className="text-[11px] font-medium px-2 py-0.5 bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200/60 dark:bg-sky-900/60 dark:text-sky-100 dark:border-sky-700 dark:hover:bg-sky-900"
                          >
                            {getIconLabel(method.icone)} {method.nome}
                          </Badge>
                        ))}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(account)}
                      className="icon-chip-green"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(account.id)}
                      className="icon-chip-red"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId && !transferModalOpen} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conta? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Records Dialog */}
      <Dialog open={transferModalOpen} onOpenChange={setTransferModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transferir Registros Relacionados</DialogTitle>
            <DialogDescription>
              Esta conta possui registros relacionados. Selecione uma conta bancária de destino para transferir todos os registros antes de excluir.
            </DialogDescription>
          </DialogHeader>
          
          {relatedRecords && (
            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-4 space-y-2">
                <h4 className="font-semibold">Registros encontrados:</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {relatedRecords.transactions > 0 && (
                    <li>• {relatedRecords.transactions} transação(ões)</li>
                  )}
                  {relatedRecords.transactionPayments > 0 && (
                    <li>• {relatedRecords.transactionPayments} pagamento(s) de transação</li>
                  )}
                  {relatedRecords.settlementConfigs > 0 && (
                    <li>• {relatedRecords.settlementConfigs} configuração(ões) de recebimento</li>
                  )}
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target-account">Conta de Destino *</Label>
                <Select value={targetAccountId} onValueChange={setTargetAccountId}>
                  <SelectTrigger id="target-account">
                    <SelectValue placeholder="Selecione uma conta bancária" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts
                      .filter((acc) => acc.id !== accountToDelete && acc.status === "ativo")
                      .map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.nome}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Todos os registros relacionados serão transferidos para esta conta antes da exclusão.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setTransferModalOpen(false);
                setAccountToDelete(null);
                setRelatedRecords(null);
                setTargetAccountId("");
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (accountToDelete && targetAccountId) {
                  transferMutation.mutate({
                    fromAccountId: accountToDelete,
                    toAccountId: targetAccountId,
                  });
                }
              }}
              disabled={!targetAccountId || transferMutation.isPending || deleteMutation.isPending}
            >
              {transferMutation.isPending || deleteMutation.isPending
                ? "Processando..."
                : "Transferir e Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
